import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  useTheme,
  useMediaQuery,
  IconButton,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Grid2,
} from '@mui/material'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import { useNavigate, useParams } from 'react-router-dom'
import projectService from '../services/projectService'
import userService from '../services/userService'
import { Project, ProjectUser } from '../services/projectService'
import AddProjectUsersDialog from '../components/project/AddProjectUsersDialog'
import PageHeader from '../components/common/PageHeader'
import SearchInput from '../components/common/Filters/SearchInput'
import SingleSelectFilter from '../components/common/Filters/SingleSelectFilter'
import CommonPagination from '../components/common/CommonPagination'
import FilterGrid2 from '../components/common/Filters/FilterGrid2'
import SearchButton from '../components/common/Filters/SearchButton'
import AddButton from '../components/common/Filters/AddButton'
enum ProjectStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

const ProjectDetails: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))

  const [loading, setLoading] = useState<boolean>(true)
  const [project, setProject] = useState<Project | null>(null)
  const [projectUsers, setProjectUsers] = useState<ProjectUser[]>([])
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [filter, setFilter] = useState({
    search: '',
    role: '',
    sortBy: 'createdAt',
    status: '',
  })
  const [inputFilter, setInputFilter] = useState({
    search: '',
    role: '',
    sortBy: 'createdAt',
    status: '',
  })
  const [usersPagination, setUsersPagination] = useState({
    total: 0,
    page: 1,
    perPage: 10,
    totalPages: 1,
  })
  const [addUsersDialogOpen, setAddUsersDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<ProjectUser | null>(null)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'info' | 'warning'
  }>({
    open: false,
    message: '',
    severity: 'info',
  })

  useEffect(() => {
    if (id) {
      fetchProjectData()
    }
  }, [id])

  useEffect(() => {
    if (id && project) {
      fetchProjectUsers()
    }
  }, [page, rowsPerPage])

  const fetchProjectData = async () => {
    try {
      setLoading(true)

      // Fetch project details using projectService with objectId
      if (id) {
        const projectData = await projectService.getProjectById(id)
        if (projectData) {
          // Add radius for UI display (mock data for now)
          const projectWithRadius = {
            ...projectData,
          }
          setProject(projectWithRadius)

          // Fetch project users using the objectId
          await fetchProjectUsers()
        }
      }

      setLoading(false)
    } catch (error) {
      console.error('Error fetching project data:', error)
      setLoading(false)
    }
  }

  const fetchProjectUsers = async (searchFilter: any = {}) => {
    try {
      if (id) {
        const params = {
          page: page, // Convert from 0-based to 1-based pagination
          perPage: rowsPerPage,
          ...searchFilter,
        }

        const usersData = await projectService.getProjectUsers(id, params)
        setProjectUsers(usersData.users)
        setUsersPagination(usersData.pagination)
      }
    } catch (error) {
      console.error('Error fetching project users:', error)
    }
  }

  const handleAddUsers = async (
    users: { username: string; role: string }[],
  ) => {
    try {
      if (id) {
        await userService.addUsersToProject(id, users)
        await fetchProjectUsers() // Refresh the user list
        setSnackbar({
          open: true,
          message: `เพิ่มสมาชิกจำนวน ${users.length} คนเรียบร้อยแล้ว`,
          severity: 'success',
        })
      }
    } catch (error) {
      console.error('Error adding users to project:', error)
      setSnackbar({
        open: true,
        message: 'เกิดข้อผิดพลาดในการเพิ่มสมาชิก',
        severity: 'error',
      })
    }
  }

  const handleBack = () => {
    navigate('/projects')
  }

  const handleEditProject = () => {
    navigate(`/projects/${id}/edit`)
  }

  const handleManageProject = () => {
    navigate(`/projects/${id}/manage`)
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  const handleDeleteUser = async (user: ProjectUser) => {
    setSelectedUser(user)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    try {
      if (id && selectedUser) {
        await projectService.deleteProjectUser(id, selectedUser.username)
        await fetchProjectUsers() // Refresh the user list
        setSnackbar({
          open: true,
          message: 'ลบผู้ใช้งานออกจากโครงการเรียบร้อยแล้ว',
          severity: 'success',
        })
        setDeleteDialogOpen(false)
        setSelectedUser(null)
      }
    } catch (error) {
      console.error('Error deleting user from project:', error)
      setSnackbar({
        open: true,
        message: 'เกิดข้อผิดพลาดในการลบผู้ใช้งาน',
        severity: 'error',
      })
    }
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
    setSelectedUser(null)
  }

  const handleSearch = async () => {
    setFilter({
      ...inputFilter,
    })
    await fetchProjectUsers(inputFilter)
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        mb: { xs: 2, sm: 2, lg: 4 },
        maxWidth: '100%',
      }}
    >
      <PageHeader
        title="ข้อมูลโครงการ"
        breadcrumb={{
          items: [
            { label: 'รายการโครงการ', onClick: handleBack },
            { label: 'ข้อมูลโครงการ' },
          ],
        }}
      />

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
            height: 400,
          }}
        >
          <CircularProgress />
        </Box>
      ) : project ? (
        <>
          <Box
            sx={{
              display: 'flex',
              alignContent: 'start',
              flexDirection: { xs: 'column', md: 'row' },
              mt: 8,
              mb: { xs: 2, sm: 2, lg: 4 },
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 2,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                width: { md: '60%' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h6">
                  ข้อมูลโครงการ : {project.projectName || project.name}
                </Typography>
                <Box sx={{}}>
                  <IconButton onClick={handleEditProject}>
                    <Box
                      component="img"
                      src="/images/icons/button-modify.png"
                      alt="View"
                      sx={{ width: 32, height: 32 }}
                    />
                  </IconButton>
                </Box>
              </Box>
              <Typography variant="body2" color="custom.grey" sx={{ mt: -1 }}>
                รหัสโครงการ:{' '}
                {project.projectCode || project.objectId.substring(0, 8)}
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontWeight: 300 }}>
                    รหัสโครงการ:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                    {project.projectCode || project.objectId.substring(0, 8)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontWeight: 300 }}>
                    ชื่อโครงการ:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                    {project.projectName || project.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontWeight: 300 }}>
                    เวลาทำงาน:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                    {project.workingTime ||
                      `${project.workStartTime} - ${project.workEndTime}`}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontWeight: 300 }}>
                    สถานที่:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                    {project.location ||
                      `${project.latitude}, ${project.longitude}`}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontWeight: 300 }}>
                    พิกัด:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                    {project.latitude}, {project.longitude}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontWeight: 300 }}>
                    รัศมี:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                    {project.radius} เมตร
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontWeight: 300 }}>
                    สถานะ:
                  </Typography>
                  <Chip
                    label={
                      project.status === ProjectStatus.NOT_STARTED
                        ? 'ยังไม่เริ่ม'
                        : project.status === ProjectStatus.IN_PROGRESS
                        ? 'กำลังดำเนินการ'
                        : project.status === ProjectStatus.COMPLETED
                        ? 'เสร็จสิ้น'
                        : 'ไม่ระบุ'
                    }
                    color={
                      project.status === ProjectStatus.IN_PROGRESS
                        ? 'success'
                        : project.status === ProjectStatus.COMPLETED
                        ? 'info'
                        : project.status === ProjectStatus.NOT_STARTED
                        ? 'warning'
                        : 'default'
                    }
                    sx={{ fontWeight: 500 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontWeight: 300 }}>
                    การใช้งาน:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 2,
                      fontWeight: 500,
                      color: project.isActive ? 'success.main' : 'error.main',
                    }}
                  >
                    {project.isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 2,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                width: { md: '40%' },
              }}
            >
              <Typography variant="h6" gutterBottom>
                แผนที่โครงการ
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  height: 400,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  overflow: 'hidden',
                  borderRadius: 1,
                }}
              >
                {project && project.latitude && project.longitude ? (
                  <iframe
                    title="Project Location Map"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    style={{ border: 0 }}
                    src={`https://maps.google.com/maps?q=${project.latitude},${project.longitude}&z=15&output=embed`}
                    allowFullScreen
                  ></iframe>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    ไม่พบข้อมูลพิกัดโครงการ
                  </Typography>
                )}
              </Box>
              {project && project.radius && (
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    รัศมีการทำงาน: {project.radius} เมตร
                  </Typography>
                  {project && project.latitude && project.longitude && (
                    <Typography variant="body2">
                      <a
                        href={`https://www.google.com/maps?q=${project.latitude},${project.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: theme.palette.primary.main,
                          textDecoration: 'none',
                        }}
                      >
                        เปิดใน Google Maps
                      </a>
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ width: '100%', mb: 4, justifyContent: 'start' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              รายชื่อบุคลากรในโครงการ : {project.projectName || project.name}
            </Typography>

            <FilterGrid2>
              <Grid2 size={{ xs: 12, sm: 3 }}>
                <SearchInput
                  placeholder="ค้นหาผู้ใช้งาน"
                  value={inputFilter.search}
                  autoChange={true}
                  onChange={(e) => {
                    console.log(e)
                    setInputFilter({
                      ...inputFilter,
                      search: e,
                    })
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 3 }}>
                <SingleSelectFilter
                  filter={{
                    label: 'ประเภทผู้ใช้งาน',
                    value: 'role',
                    options: [
                      { label: 'ผู้ใช้งาน', value: 'user' },
                      { label: 'ผู้ดูแล', value: 'admin' },
                      { label: 'พนักงาน', value: 'employee' },
                    ],
                  }}
                  value={inputFilter.role}
                  onChange={(e) => {
                    setInputFilter({
                      ...inputFilter,
                      role: e?.target?.value as string,
                    })
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 3 }}>
                <SingleSelectFilter
                  filter={{
                    label: 'เรียงลำดับ',
                    value: 'role',
                    options: [
                      { label: 'ลำดับตามชื่อ', value: 'name' },
                      { label: 'ลำดับตามวันที่สร้าง', value: 'createdAt' },
                    ],
                  }}
                  value={inputFilter.sortBy}
                  onChange={(e) => {
                    setInputFilter({
                      ...inputFilter,
                      sortBy: e?.target?.value as string,
                    })
                  }}
                />
              </Grid2>
              <Grid2>
                <SearchButton onClick={handleSearch} />
              </Grid2>
              <Grid2>
                <AddButton
                  label="เพิ่มสมาชิก"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setAddUsersDialogOpen(true)}
                />
              </Grid2>
            </FilterGrid2>

            {projectUsers.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 200,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  ไม่พบข้อมูลพนักงานในโครงการนี้
                </Typography>
              </Box>
            ) : (
              <>
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">ลำดับ</TableCell>
                          <TableCell align="center">รหัสพนักงาน</TableCell>
                          <TableCell align="center">ชื่อ-นามสกุล</TableCell>
                          <TableCell align="center" sx={{ width: '10%' }}>
                            ชื่อผู้ใช้
                          </TableCell>
                          <TableCell align="center">เบอร์โทรศัพท์</TableCell>
                          <TableCell align="center">ประเภทผู้ใช้งาน</TableCell>
                          <TableCell align="center">สถานะ</TableCell>
                          <TableCell align="center">จัดการ</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {projectUsers.map((user, index) => (
                          <TableRow key={user._id}>
                            <TableCell align="center">
                              {(usersPagination.page - 1) *
                                usersPagination.perPage +
                                index +
                                1}
                            </TableCell>
                            <TableCell align="center">
                              {user?.user?.employeeCode || '-'}
                            </TableCell>
                            <TableCell align="center">
                              {user?.user?.name || '-'}
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                <Avatar sx={{ mr: 1, width: 40, height: 40 }}>
                                  {user?.user?.username
                                    ?.charAt(0)
                                    .toUpperCase() || 'A'}
                                </Avatar>
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 'bold' }}
                                  >
                                    {user?.user?.username || '-'}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              {user?.user?.phoneNumber || '-'}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={
                                  user?.user?.roles?.includes('admin')
                                    ? 'ผู้ดูแล'
                                    : user?.user?.roles?.includes('employee')
                                    ? 'พนักงาน'
                                    : 'สมาชิก'
                                }
                                color={
                                  user?.user?.roles?.includes('admin')
                                    ? 'primary'
                                    : 'default'
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={
                                  user?.user?.isEnable ? 'ใช้งาน' : 'ไม่ใช้งาน'
                                }
                                color={
                                  user?.user?.isEnable ? 'success' : 'error'
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteUser(user)}
                                disabled={user?.role === 'admin'}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </>
            )}
          </Box>
          <CommonPagination
            count={Math.ceil(usersPagination.total / rowsPerPage)}
            page={usersPagination.page}
            rowsPerPage={rowsPerPage}
            onChange={(event, value) => {
              handleChangePage(event, value)
            }}
            onRowsPerPageChange={(value) => {
              handleChangeRowsPerPage({
                target: { value: value.toString() },
              } as React.ChangeEvent<HTMLInputElement>)
            }}
            totalItems={usersPagination.total}
          />

          {/* Add Users Dialog */}
          <AddProjectUsersDialog
            open={addUsersDialogOpen}
            onClose={() => setAddUsersDialogOpen(false)}
            projectId={id || ''}
            onAddUsers={handleAddUsers}
          />

          {/* Delete User Confirmation Dialog */}
          <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
            <DialogTitle>ยืนยันการลบผู้ใช้งาน</DialogTitle>
            <DialogContent>
              <Typography>
                คุณต้องการลบผู้ใช้งาน {selectedUser?.username}{' '}
                ออกจากโครงการนี้ใช่หรือไม่?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancelDelete}>ยกเลิก</Button>
              <Button
                onClick={handleConfirmDelete}
                color="error"
                variant="contained"
              >
                ลบ
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </>
      ) : (
        <Typography variant="h6" color="error">
          ไม่พบข้อมูลโครงการ
        </Typography>
      )}
    </Box>
  )
}

export default ProjectDetails
